import { collectEmbeddedPostCardReferences } from '~/utils/embeddedPostCards'
import type { EmbeddedPostCardMap, EmbeddedPostCardReference } from '~/utils/embeddedPostCards'

type EmbeddedPostCardOptions = {
  source: () => string
  fields?: Record<string, unknown>
  siteBaseUrl: string
}

// useEmbeddedPostCards 管理 Markdown 中站内文章卡片的元信息加载
export function useEmbeddedPostCards(options: EmbeddedPostCardOptions) {
  const blogApi = useBlogApi()
  const postCards = ref<EmbeddedPostCardMap>({})
  let mounted = false
  let requestVersion = 0

  // load 加载去重后的文章卡片元信息
  const load = async (references: EmbeddedPostCardReference[], version: number) => {
    const groups = new Map<string, EmbeddedPostCardReference[]>()
    for (const reference of references) {
      groups.set(reference.key, [...(groups.get(reference.key) || []), reference])
    }
    await Promise.all([...groups.values()].map(async group => {
      const target = group[0]
      const item = target.mode === 'id'
        ? await blogApi.getContentMeta(target.id, { throwError: false })
        : await blogApi.getContentMetaBySlug(target.slug, { throwError: false })
      if (version !== requestVersion) return
      const state = item?.ID && item.type === 'post'
        ? { status: 'ready' as const, content: item }
        : { status: 'failed' as const }
      postCards.value = { ...postCards.value, ...Object.fromEntries(group.map(reference => [reference.href, state])) }
    }))
  }

  // refresh 根据当前 Markdown 内容重建卡片状态
  const refresh = () => {
    const version = ++requestVersion
    const origins = import.meta.client ? [options.siteBaseUrl, window.location.origin] : [options.siteBaseUrl]
    const references = collectEmbeddedPostCardReferences(options.source(), options.fields, origins)
    postCards.value = Object.fromEntries(references.map(reference => [reference.href, { status: 'loading' as const }]))
    if (mounted && references.length) void load(references, version)
  }

  watch(options.source, refresh, { immediate: true })
  onMounted(() => {
    mounted = true
    refresh()
  })
  onBeforeUnmount(() => {
    requestVersion += 1
  })

  return { postCards }
}
